package com.sddamico.swigmore.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.swigmore.api.ApiException
import com.sddamico.swigmore.api.PollApi
import com.sddamico.swigmore.api.ref
import com.sddamico.swigmore.components.admin.BallotEditor
import com.sddamico.swigmore.components.admin.MembersPanel
import com.sddamico.swigmore.components.layouts.Column
import com.sddamico.swigmore.components.layouts.Columns
import com.sddamico.swigmore.components.layouts.ContentColumn
import com.sddamico.swigmore.components.layouts.SignedInPage
import com.sddamico.swigmore.ds.Reveal
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.core.Card
import com.sddamico.swigmore.ds.core.CardVariant
import com.sddamico.swigmore.ds.core.PageTitle
import com.sddamico.swigmore.ds.core.PanelLabel
import com.sddamico.swigmore.ds.core.SubmissionStatus
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.feedback.Banner
import com.sddamico.swigmore.ds.feedback.FeedRow
import com.sddamico.swigmore.ds.feedback.PageTitleSkeleton
import com.sddamico.swigmore.ds.feedback.Skeleton
import com.sddamico.swigmore.ds.feedback.SkeletonBlock
import com.sddamico.swigmore.ds.feedback.StatTile
import com.sddamico.swigmore.ds.forms.Button
import com.sddamico.swigmore.ds.forms.ButtonStyle
import com.sddamico.swigmore.ds.forms.ButtonVariant
import com.sddamico.swigmore.ds.forms.Toggle
import com.sddamico.swigmore.ds.poll.SubmissionRow
import com.sddamico.swigmore.ds.poll.SubmissionTable
import com.sddamico.swigmore.ds.typeBody
import com.sddamico.swigmore.ds.typeCaps
import com.sddamico.swigmore.ds.typeDisplay
import com.sddamico.swigmore.ds.typeMono
import com.sddamico.swigmore.models.ActivityDto
import com.sddamico.swigmore.models.AdminBallotDto
import com.sddamico.swigmore.models.AdminBallotsResponse
import com.sddamico.swigmore.models.BallotAction
import com.sddamico.swigmore.models.BallotStatus
import com.sddamico.swigmore.models.FantraxSyncState
import com.sddamico.swigmore.models.LeagueResponse
import com.sddamico.swigmore.models.MemberDto
import com.sddamico.swigmore.models.PollResultsResponse
import com.sddamico.swigmore.models.PollTeamDto
import com.sddamico.swigmore.models.PollWeekDto
import com.sddamico.swigmore.models.WeekStatus
import com.sddamico.swigmore.util.ApiResource
import com.sddamico.swigmore.util.AppTab
import com.sddamico.swigmore.util.LoadState
import com.sddamico.swigmore.util.Routes
import com.sddamico.swigmore.util.rememberAction
import com.sddamico.swigmore.util.rememberApiResource
import com.sddamico.swigmore.util.errorText
import com.sddamico.swigmore.util.rememberNow
import com.sddamico.swigmore.util.untilCutoff
import com.sddamico.swigmore.util.shortDayTime
import com.sddamico.swigmore.util.wireAge
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Date

/** The commissioner's desk: this week's ballots, who hasn't voted, the submission wire, Fantrax and members. */
@Page
@Composable
fun AdminPage() {
  SignedInPage("Commish", activeTab = AppTab.Admin, adminOnly = true) { signedIn ->
    ContentColumn { CommishDesk(signedIn.league) }
  }
}

private class AdminData(
  val ballots: AdminBallotsResponse,
  val teams: List<PollTeamDto>,
  val members: List<MemberDto>,
  val games: Int,
  /** A published week re-tallied from the current ballots, which only reach owners on republish. */
  val liveTally: PollResultsResponse?,
)

/** Who the ballot editor is open for; [ballot] is null for a new (late) ballot. */
private class EditTarget(val voter: MemberDto, val ballot: AdminBallotDto?)

private suspend fun loadAdminData(): AdminData = coroutineScope {
  val members = async { PollApi.members() }
  val slate = async { PollApi.matchups() }
  val current = async { PollApi.current() }
  val ballots = PollApi.ballots()
  val week = ballots.week
  val liveTally = if (week?.status == WeekStatus.PUBLISHED) PollApi.results(week.ref, preview = true) else null
  AdminData(ballots, current.await().teams, members.await(), slate.await().matchups.size, liveTally)
}

@Composable
private fun CommishDesk(leagueResource: ApiResource<LeagueResponse>) {
  val ctx = rememberPageContext()
  val desk = rememberApiResource { loadAdminData() }
  // One action for the whole desk, so only one change or sync runs at a time and one notice shows.
  val action = rememberAction()
  var syncing by remember { mutableStateOf(false) }
  var editing by remember { mutableStateOf<EditTarget?>(null) }
  val now = rememberNow(30_000)

  /** Never throws: a change that went through stays done when the page can't refresh after it. */
  suspend fun reload() {
    try {
      coroutineScope {
        launch { desk.reload() }
        launch { leagueResource.reload() }
      }
    } catch (e: CancellationException) {
      throw e
    } catch (e: Throwable) {
      action.error = e.errorText()
    }
  }

  fun act(op: suspend () -> Unit) = action.run {
    try {
      op()
    } finally {
      reload()
    }
  }

  val failed = desk.state as? LoadState.Failed ?: leagueResource.state as? LoadState.Failed
  if (failed != null) {
    PageTitle("Commissioner's desk", "The Ballot Box", lede = failed.message)
    return
  }
  val data = desk.value
  val league = leagueResource.value
  if (data == null || league == null) return DeskSkeleton()
  Reveal {
    val week = data.ballots.week
    // The server closes the week at the deadline; reload then so Publish unlocks without a refresh.
    val openUntil = week?.closesAt?.takeIf { week.status == WeekStatus.OPEN }
    LaunchedEffect(openUntil) {
      val closesAt = openUntil ?: return@LaunchedEffect
      delay((closesAt - Date.now().toLong()).coerceAtLeast(0) + 1_000)
      action.run { reload() }
    }
    // The scheduler publishes a closed week on its next run; keep checking so Send appears without a refresh.
    val closed = week?.status == WeekStatus.CLOSED
    LaunchedEffect(closed) {
      while (closed) {
        delay(15_000)
        reload()
      }
    }
    val teamsById = data.teams.associateBy { it.id }
    fun MemberDto.label() = teamId?.let { teamsById[it]?.name } ?: displayName

    Div(
      attrs = Modifier.css("display" to "flex", "flex-wrap" to "wrap", "justify-content" to "space-between",
        "align-items" to "flex-end", "gap" to "16px", "margin-bottom" to "22px").toAttrs()
    ) {
      PageTitle(
        eyebrow = "Commissioner's desk" + (week?.let { " · Week ${it.week}" } ?: ""),
        title = "The Ballot Box",
        lede = if (week == null) "No poll has been opened yet." else null,
      )
      Div(attrs = Modifier.css("display" to "flex", "gap" to "8px", "flex-wrap" to "wrap").toAttrs()) {
        data.ballots.openableWeek?.let { openable ->
          Button("Open week $openable poll", variant = ButtonVariant.Gold, style = ButtonStyle.Caps, disabled = action.busy, onClick = {
            act { PollApi.openWeek(openable) }
          })
        }
        if (week != null) {
          Button("Preview announcement", variant = ButtonVariant.Outline, style = ButtonStyle.Caps, onDark = true, onClick = {
            ctx.router.navigateTo(Routes.announce(week.ref))
          })
          if (week.status == WeekStatus.PUBLISHED && !data.ballots.announcementSent) {
            Button("Send announcement", variant = ButtonVariant.Gold, style = ButtonStyle.Caps, disabled = action.busy, onClick = {
              if (window.confirm("Send the week ${week.week} announcement to every owner by email and push?")) {
                act { PollApi.sendAnnouncement(week.ref) }
              }
            })
          }
          PublishButton(week, action.busy) { republish ->
            val prompt = when {
              republish -> "Republish the week ${week.week} poll with the current ballots?"
              data.ballots.autoSend -> "Publish the week ${week.week} poll? Owners get the announcement by email and push."
              else -> "Publish the week ${week.week} poll? Auto-send is off, so the announcement waits until you send it."
            }
            if (window.confirm(prompt)) act { PollApi.publish(week.ref) }
          }
        }
      }
    }

    action.error?.let { Banner(it, Modifier.css("margin-bottom" to "16px")) }

    val ballots = data.ballots
    if (week != null) {
      Div(
        attrs = Modifier.css("display" to "grid", "grid-template-columns" to "repeat(auto-fit,minmax(160px,1fr))",
          "gap" to "10px", "margin-bottom" to "22px").toAttrs()
      ) {
        // Ballots from removed members still count in the tally but not toward who's left to vote.
        StatTile("${data.members.size - ballots.notVoted.size}/${data.members.size}", "Ballots in")
        StatTile("${ballots.notVoted.size}", "Missing")
        when (week.status) {
          WeekStatus.OPEN -> StatTile(untilCutoff(week.closesAt, now), "Until cutoff")
          WeekStatus.CLOSED -> StatTile("Locked", "Awaiting publish")
          WeekStatus.PUBLISHED -> StatTile("Final", "Published ${week.publishedAt?.let { shortDayTime(it) }}" +
            if (ballots.announcementSent) "" else " · email not sent")
        }
      }
    }

    Columns(gap = "24px") {
      Column("580px") {
        val target = editing
        if (target != null && week != null) {
          BallotEditor(
            title = when {
              target.ballot != null -> "Edit ballot · Week ${week.week}"
              week.status == WeekStatus.OPEN -> "Add ballot · Week ${week.week}"
              else -> "Late ballot · Week ${week.week}"
            },
            voterName = target.voter.label(),
            teams = ballots.teams,
            initial = target.ballot?.teamIds.orEmpty(),
            save = { teamIds ->
              PollApi.saveBallot(week.ref, target.voter.email, teamIds)
              // Closing the editor cancels its scope, which this runs in, so reload first.
              reload()
              editing = null
            },
            onCancel = { editing = null },
          )
        }
        data.liveTally?.let { LiveTallyCard(it, changed = data.ballots.liveTallyChanged) }
        if (week != null) {
          SubmissionTable(footer = "Ballot contents visible to commish only") {
            if (ballots.ballots.isEmpty()) {
              Div(attrs = Modifier.typeBody("14px", 500, "1.4").css("padding" to "14px 16px", "border-top" to "1px solid ${SwColor.ruleOnCream}").toAttrs()) {
                Text("No ballots yet.")
              }
            }
            ballots.ballots.sortedByDescending { it.updatedAt }.forEach { ballot ->
              SubmissionRow(
                team = ballot.voter.label(),
                topPick = ballot.teamIds.firstOrNull()?.let { teamsById[it]?.name ?: it } ?: "—",
                submittedAt = shortDayTime(ballot.updatedAt),
                status = when (ballot.status) {
                  BallotStatus.ON_TIME -> SubmissionStatus.OnTime
                  BallotStatus.AMENDED -> SubmissionStatus.Amended
                  BallotStatus.LATE -> SubmissionStatus.Late
                },
                disabled = action.busy,
                onEdit = { editing = EditTarget(ballot.voter, ballot) },
                onDelete = {
                  if (window.confirm("Delete ${ballot.voter.label()}'s ballot? It can't be restored.")) act {
                    PollApi.deleteBallot(ballot.id)
                  }
                },
              )
            }
          }
        }
      }
      Column("320px", Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "16px")) {
        AutoSendPanel(ballots.autoSend, action.busy) { on -> act { PollApi.setAutoSend(on) } }
        if (week != null) {
          Card(variant = CardVariant.Field) {
            PanelLabel("Still in the locker room")
            if (ballots.notVoted.isEmpty()) FeedRow("Every owner has voted.")
            ballots.notVoted.forEach { member ->
              FeedRow(member.label(), trailing = {
                Button(if (week.status == WeekStatus.OPEN) "Add ballot" else "Add late ballot",
                  Modifier.css("color" to SwColor.gold, "padding" to "0"),
                  variant = ButtonVariant.Ghost, style = ButtonStyle.CapsSm, onDark = true,
                  onClick = { editing = EditTarget(member, null) })
              })
            }
          }
          Card(variant = CardVariant.Field) {
            PanelLabel("Wire · every submission")
            if (ballots.activity.isEmpty()) FeedRow("Nothing yet this week.")
            ballots.activity.forEach { FeedRow(it.describe(it.voter.label()), meta = wireAge(it.at, now)) }
          }
        }
        FantraxPanel(league, data.games, syncing, action.busy) {
          action.run {
            syncing = true
            try {
              PollApi.syncFantrax()
              for (attempt in 1..60) {
                delay(3_000)
                if (runCatching { leagueResource.reload() }.isSuccess && leagueResource.value?.sync?.running == false) break
              }
            } catch (e: ApiException) {
              // 409: a sync is already running; the reload below shows its state.
              if (e.status != 409) throw e
            } finally {
              syncing = false
              reload()
            }
          }
        }
      }
    }

    Div(attrs = Modifier.css("margin-top" to "32px").toAttrs()) {
      MembersPanel(
        members = data.members,
        teams = data.teams,
        save = { member ->
          PollApi.saveMember(member)
          reload()
        },
        remove = { member ->
          PollApi.deleteMember(member.email)
          reload()
        },
      )
    }
  }
}

@Composable
private fun PublishButton(week: PollWeekDto, busy: Boolean, onPublish: (republish: Boolean) -> Unit) {
  when (week.status) {
    WeekStatus.OPEN -> Button("Publish · after cutoff", variant = ButtonVariant.Gold, style = ButtonStyle.Caps, disabled = true)
    WeekStatus.CLOSED -> Button("Publish week ${week.week} poll", variant = ButtonVariant.Gold, style = ButtonStyle.Caps,
      disabled = busy, onClick = { onPublish(false) })
    WeekStatus.PUBLISHED -> Button("Republish week ${week.week}", variant = ButtonVariant.Gold, style = ButtonStyle.Caps,
      disabled = busy, onClick = { onPublish(true) })
  }
}

@Composable
private fun AutoSendPanel(autoSend: Boolean, busy: Boolean, onChange: (Boolean) -> Unit) {
  Card(variant = CardVariant.Field) {
    PanelLabel("Results email")
    Toggle("Auto-send", autoSend, disabled = busy, onChange = onChange)
    FeedRow(
      if (autoSend) "When ballots close, the poll publishes and the announcement goes to every owner by email and push."
      else "Held. The poll still publishes when ballots close, and the email and push wait until you send them.",
      Modifier.css("margin-top" to "8px"),
    )
  }
}

@Composable
private fun LiveTallyCard(live: PollResultsResponse, changed: Boolean) {
  Card(variant = CardVariant.Field, modifier = Modifier.css("margin-bottom" to "16px")) {
    PanelLabel("Live tally", note = "${live.ballotCount} BALLOTS")
    if (!changed) {
      Div(attrs = Modifier.typeBody("13px", 500, "1.4").toAttrs()) { Text("Matches the published poll.") }
      return@Card
    }
    Div(attrs = Modifier.typeBody("13px", 500, "1.4").css("margin-bottom" to "8px").toAttrs()) {
      Text("Ballots changed since this poll was published. Republish to apply these standings.")
    }
    live.ranked.forEach { t ->
      Div(
        attrs = Modifier.css("display" to "grid", "grid-template-columns" to "30px minmax(0,1fr) auto", "gap" to "10px",
          "align-items" to "center", "padding" to "6px 0", "border-top" to "1px solid ${SwColor.hairlineSoft}").toAttrs()
      ) {
        Span(attrs = Modifier.typeDisplay("18px").css("text-align" to "center").toAttrs()) { Text("${t.rank}") }
        Span(attrs = Modifier.typeCaps("12px", "0").toAttrs()) { Text(t.team.name) }
        Span(attrs = Modifier.typeMono("11px").toAttrs()) {
          Text("${t.points} PTS" + if (t.firstPlaceVotes > 0) " · ${t.firstPlaceVotes} 1ST" else "")
        }
      }
    }
  }
}

@Composable
private fun FantraxPanel(league: LeagueResponse, games: Int, syncing: Boolean, busy: Boolean, onSync: () -> Unit) {
  val sync = league.sync
  Card(variant = CardVariant.Field) {
    PanelLabel("Fantrax sync")
    Div(attrs = Modifier.typeMono("11px", lineHeight = "1.7").css("color" to SwColor.textSecondary).toAttrs()) {
      val lastSuccess = sync.lastSuccessAt
      if (lastSuccess == null) {
        Div { Text("no successful sync yet") }
      } else {
        Div { Text("standings ✓ ${league.teams.size} teams · ${shortDayTime(lastSuccess)} PT") }
        sync.currentWeek?.let { Div { Text("week $it schedule ✓ $games games") } }
      }
      if (sync.lastAttemptState == FantraxSyncState.FAILED) {
        Div(attrs = Modifier.css("color" to SwColor.gold).toAttrs()) {
          Text("last attempt failed" + (sync.lastError?.let { ": $it" } ?: ""))
        }
      }
    }
    Button(if (syncing || sync.running) "Syncing…" else "Sync now", Modifier.css("margin-top" to "10px"),
      variant = ButtonVariant.Outline, style = ButtonStyle.CapsSm, onDark = true,
      disabled = busy || sync.running, onClick = onSync)
  }
}

private fun ActivityDto.describe(name: String): String {
  val actor = actorEmail?.substringBefore('@')
  return when (action) {
    BallotAction.SUBMITTED -> if (actor != null) "$actor entered $name's ballot" else "$name submitted"
    BallotAction.AMENDED -> if (actor != null) "$actor edited $name's ballot" else "$name amended their ballot"
    BallotAction.WITHDRAWN -> if (actor != null) "$actor deleted $name's ballot" else "$name withdrew their ballot"
  }
}

@Composable
private fun DeskSkeleton() {
  Skeleton {
    PageTitleSkeleton(Modifier.css("margin-bottom" to "22px"), lede = false)
    Div(
      attrs = Modifier.css("display" to "grid", "grid-template-columns" to "repeat(auto-fit,minmax(160px,1fr))",
        "gap" to "10px", "margin-bottom" to "22px").toAttrs()
    ) {
      repeat(3) { SkeletonBlock("84px") }
    }
    Columns(gap = "24px") {
      Column("580px") { SkeletonBlock("360px") }
      Column("320px") { SkeletonBlock("240px") }
    }
  }
}
